/* eslint-disable max-lines */
/**
 * Telemetry Service for User Behavior and Performance Tracking
 *
 * Implements privacy-compliant event collection with batching and efficient transmission.
 * Supports both authenticated and anonymous events for comprehensive analytics.
 */

import { z } from "zod";

// Telemetry Event Schema
export const TelemetryEvent = z.object({
  id: z.string(),
  userId: z.string().nullable().optional(), // null for anonymous events
  sessionId: z.string(), // browser/app session identifier
  timestamp: z.string(), // ISO timestamp
  type: z.string(), // event category
  action: z.string(), // specific action
  assetId: z.string().nullable().optional(), // content reference
  metadata: z.record(z.unknown()).optional(), // event-specific data
});

export type ApiTelemetryEvent = z.infer<typeof TelemetryEvent>;

// Batch submission schema
export const TelemetryBatch = z.object({
  events: z.array(TelemetryEvent),
  sessionId: z.string(),
  timestamp: z.string(), // batch timestamp
});

export type ApiTelemetryBatch = z.infer<typeof TelemetryBatch>;

// Batch response schema
export const TelemetryBatchResponse = z.object({
  received: z.number(), // number of events received
  processed: z.number(), // number of events successfully processed
  errors: z.array(z.string()).optional(), // any processing errors
});

export type ApiTelemetryBatchResponse = z.infer<typeof TelemetryBatchResponse>;

// Common event types
export const EventType = {
  // Playback events
  PLAYBACK: "playback",
  // User journey events
  AUTH: "auth",
  NAVIGATION: "navigation",
  CONTENT: "content",
  // Performance events
  PERFORMANCE: "performance",
  // Search events
  SEARCH: "search",
} as const;

// Common event actions
export const EventAction = {
  // Playback actions (TELEM-03: aligned to video_* taxonomy)
  START: "video_start",
  PAUSE: "video_pause",
  RESUME: "video_resume",
  SEEK: "video_seek",
  COMPLETE: "video_complete",
  ABANDON: "video_abandon",
  QUALITY_CHANGE: "video_quality_change",
  ERROR: "video_error",
  // Auth actions
  SIGNUP: "signup",
  SIGNIN: "signin",
  SIGNOUT: "signout",
  // Navigation actions
  PAGE_VIEW: "page_view",
  // Content actions
  TITLE_CLICK: "title_click",
  LIKE: "like",
  UNLIKE: "unlike",
  ADD_TO_WATCHLIST: "add_to_watchlist",
  REMOVE_FROM_WATCHLIST: "remove_from_watchlist",
  // Performance actions
  PAGE_LOAD: "page_load",
  VIDEO_LOAD: "video_load",
  BUFFER: "buffer",
  // Search actions
  QUERY: "query",
  SUGGESTION_CLICK: "suggestion_click",
  RESULT_CLICK: "result_click",
} as const;

// Configuration for telemetry service
export interface TelemetryConfig {
  apiBase: string;
  batchSize: number; // max events per batch
  batchTimeoutMs: number; // max time between batches
  maxRetries: number; // max retry attempts for failed batches
  retryDelayMs: number; // delay between retries
  enabled: boolean; // global enable/disable
  debug: boolean; // enable debug logging
}

// Default configuration
const DEFAULT_CONFIG: TelemetryConfig = {
  apiBase: "http://localhost:3333",
  batchSize: 50,
  batchTimeoutMs: 30000, // 30 seconds
  maxRetries: 3,
  retryDelayMs: 1000, // 1 second
  enabled: true,
  debug: false,
};

/**
 * Telemetry Service Class
 *
 * Handles event collection, batching, and transmission with privacy compliance.
 */
export class TelemetryService {
  private events: ApiTelemetryEvent[] = [];
  private readonly sessionId: string;
  private userId: string | null = null;
  private readonly config: TelemetryConfig;
  private batchTimer: NodeJS.Timeout | null = null;
  private lastSendTime: number = 0;
  private accessToken?: string;

  constructor(config: Partial<TelemetryConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.sessionId = this.generateSessionId();

    if (this.config.debug) {
      console.log("TelemetryService initialized with config:", this.config);
    }
  }

  /**
   * Set current user for authenticated events
   */
  setUser(userId: string | null, accessToken?: string): void {
    this.userId = userId;
    this.accessToken = accessToken;

    if (this.config.debug) {
      console.log("TelemetryService user set:", userId ? `user:${userId}` : "anonymous");
    }
  }

  /**
   * Track a telemetry event
   */
  track(type: string, action: string, metadata?: Record<string, unknown>, assetId?: string): void {
    if (!this.config.enabled) {
      return;
    }

    const event: ApiTelemetryEvent = {
      id: this.generateEventId(),
      userId: this.userId,
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      type,
      action,
      assetId: assetId || null,
      metadata: metadata || {},
    };

    this.events.push(event);

    if (this.config.debug) {
      console.log("TelemetryService tracked event:", { type, action, assetId, metadata });
    }

    // Check if we should send immediately
    this.maybeSend();
  }

  /**
   * Track playback events with common metadata
   */
  trackPlayback(action: string, assetId: string, metadata: Record<string, unknown> = {}): void {
    this.track(
      EventType.PLAYBACK,
      action,
      {
        timestamp: Date.now(),
        userAgent: globalThis.navigator?.userAgent,
        ...metadata,
      },
      assetId,
    );
  }

  /**
   * Track navigation events
   */
  trackNavigation(action: string, metadata: Record<string, unknown> = {}): void {
    this.track(EventType.NAVIGATION, action, {
      url: globalThis.window?.location?.href,
      referrer: globalThis.document?.referrer,
      ...metadata,
    });
  }

  /**
   * Track content interaction events
   */
  trackContent(action: string, titleId?: string, metadata: Record<string, unknown> = {}): void {
    this.track(EventType.CONTENT, action, metadata, titleId);
  }

  /**
   * Track search events
   */
  trackSearch(action: string, metadata: Record<string, unknown> = {}): void {
    this.track(EventType.SEARCH, action, metadata);
  }

  /**
   * Track performance events
   */
  trackPerformance(action: string, metadata: Record<string, unknown> = {}): void {
    this.track(EventType.PERFORMANCE, action, {
      timestamp: Date.now(),
      ...metadata,
    });
  }

  /**
   * Force send all pending events
   */
  async flush(): Promise<void> {
    if (this.events.length === 0) {
      return;
    }

    await this.sendBatch();
  }

  /**
   * Check if we should send events based on batch size or timeout
   */
  private maybeSend(): void {
    const shouldSendBySize = this.events.length >= this.config.batchSize;
    const shouldSendByTime = Date.now() - this.lastSendTime > this.config.batchTimeoutMs;

    if (shouldSendBySize) {
      void this.sendBatch();
    } else if (!this.batchTimer && shouldSendByTime) {
      this.scheduleBatch();
    }
  }

  /**
   * Schedule batch sending after timeout
   */
  private scheduleBatch(): void {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
    }

    this.batchTimer = setTimeout(() => {
      void this.sendBatch();
      this.batchTimer = null;
    }, this.config.batchTimeoutMs);
  }

  /**
   * Send current batch of events
   */
  private async sendBatch(): Promise<void> {
    if (this.events.length === 0) {
      return;
    }

    const batch: ApiTelemetryBatch = {
      events: [...this.events], // copy events
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
    };

    // Clear events immediately to prevent duplicates
    this.events = [];
    this.lastSendTime = Date.now();

    try {
      await this.transmitBatch(batch);

      if (this.config.debug) {
        console.log(`TelemetryService sent batch: ${batch.events.length} events`);
      }
    } catch (error) {
      if (this.config.debug) {
        console.error("TelemetryService batch send failed:", error);
      }

      // On failure, add events back to queue for retry
      this.events.unshift(...batch.events);

      // For now, we'll just lose the events to prevent memory buildup
      // Future enhancement: Implement retry logic with exponential backoff
    }
  }

  /**
   * Transmit batch to API
   */
  private async transmitBatch(batch: ApiTelemetryBatch): Promise<void> {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    // Add authorization if available
    if (this.accessToken) {
      headers.Authorization = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${this.config.apiBase}/v1/events`, {
      method: "POST",
      headers,
      body: JSON.stringify(batch),
    });

    if (!response.ok) {
      throw new Error(`Telemetry batch failed: ${response.status} ${response.statusText}`);
    }

    // Validate response
    const result: unknown = await response.json();
    TelemetryBatchResponse.parse(result);
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    try {
      // Use crypto.randomUUID() when available for better randomness
      if (typeof crypto !== "undefined" && crypto.randomUUID) {
        const uuid = crypto.randomUUID();
        // Split and join to avoid ESLint replaceAll rule
        return `session_${Date.now()}_${uuid.split("-").join("").substring(0, 13)}`;
      }
    } catch {
      // Fallback to Math.random() for telemetry use case
    }

    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`; // telemetry IDs don't require cryptographic randomness
  }

  /**
   * Generate unique event ID
   */
  private generateEventId(): string {
    try {
      // Use crypto.randomUUID() when available for better randomness
      if (typeof crypto !== "undefined" && crypto.randomUUID) {
        const uuid = crypto.randomUUID();
        return `event_${Date.now()}_${uuid.split("-").join("").substring(0, 13)}`;
      }
    } catch {
      // Fallback to Math.random() for telemetry use case
    }

    return `event_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`; // telemetry IDs don't require cryptographic randomness
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    // Try to send remaining events
    if (this.events.length > 0) {
      this.sendBatch().catch(() => {
        // Ignore errors during cleanup
      });
    }
  }
}

/**
 * Global telemetry instance
 * Initialize with createTelemetryService() in your app
 */
let globalTelemetryService: TelemetryService | null = null;

/**
 * Create and configure global telemetry service
 */
export function createTelemetryService(config: Partial<TelemetryConfig> = {}): TelemetryService {
  if (globalTelemetryService) {
    globalTelemetryService.destroy();
  }

  globalTelemetryService = new TelemetryService(config);
  return globalTelemetryService;
}

/**
 * Get global telemetry service instance
 */
export function getTelemetryService(): TelemetryService | null {
  return globalTelemetryService;
}

/**
 * Convenience functions for common tracking patterns
 */
export const telemetry = {
  /**
   * Track video playback start
   */
  trackVideoStart(assetId: string, metadata: Record<string, unknown> = {}) {
    globalTelemetryService?.trackPlayback(EventAction.START, assetId, metadata);
  },

  /**
   * Track video playback pause
   */
  trackVideoPause(assetId: string, position: number) {
    globalTelemetryService?.trackPlayback(EventAction.PAUSE, assetId, { position });
  },

  /**
   * Track video playback resume
   */
  trackVideoResume(assetId: string, position: number) {
    globalTelemetryService?.trackPlayback(EventAction.RESUME, assetId, { position });
  },

  /**
   * Track video seek
   */
  trackVideoSeek(assetId: string, fromPosition: number, toPosition: number) {
    globalTelemetryService?.trackPlayback(EventAction.SEEK, assetId, {
      fromPosition,
      toPosition,
      seekDistance: toPosition - fromPosition,
    });
  },

  /**
   * Track video completion
   */
  trackVideoComplete(assetId: string, duration: number) {
    globalTelemetryService?.trackPlayback(EventAction.COMPLETE, assetId, { duration });
  },

  /**
   * Track video abandonment
   */
  trackVideoAbandon(assetId: string, position: number, duration: number) {
    globalTelemetryService?.trackPlayback(EventAction.ABANDON, assetId, {
      position,
      duration,
      completionRate: duration > 0 ? position / duration : 0,
    });
  },

  /**
   * Track page navigation
   */
  trackPageView(path: string, title?: string) {
    globalTelemetryService?.trackNavigation(EventAction.PAGE_VIEW, { path, title });
  },

  /**
   * Track title card click
   */
  trackTitleClick(titleId: string, position?: number, rail?: string) {
    globalTelemetryService?.trackContent(EventAction.TITLE_CLICK, titleId, {
      position,
      rail,
    });
  },

  /**
   * Track search query
   */
  trackSearch(query: string, resultsCount?: number) {
    globalTelemetryService?.trackSearch(EventAction.QUERY, {
      query: query.toLowerCase(), // normalize for privacy
      queryLength: query.length,
      resultsCount,
    });
  },

  /**
   * Track search result click
   */
  trackSearchResultClick(titleId: string, position: number, query: string) {
    globalTelemetryService?.trackSearch(EventAction.RESULT_CLICK, {
      titleId,
      position,
      query: query.toLowerCase(),
    });
  },
};
